import type { DateLength } from '@/api/types';

/**
 * v9 §A2 ready-to-send messages: one tap copies or shares a line with the
 * link, so sending a Popin date costs the sender nothing. Warm, never pushy;
 * four tones × four lines. `{len}` is the date length in minutes.
 */
export type Tone = 'sweet' | 'funny' | 'direct' | 'hinglish';

export const TONES: { id: Tone; label: string; emoji: string }[] = [
  { id: 'sweet', label: 'Sweet', emoji: '💛' },
  { id: 'funny', label: 'Funny', emoji: '😄' },
  { id: 'direct', label: 'Direct', emoji: '⚡' },
  { id: 'hinglish', label: 'Hinglish', emoji: '☕' },
];

export const READY_MESSAGES: Record<Tone, string[]> = {
  sweet: [
    'I’d love to see you smile before we meet — {len}-min video date on Popin? 💛',
    'Before we meet IRL, a little video date? Popin guides it, so it’s easy.',
    'Pick any time that suits you — I’d really like to talk properly first ✨',
    'Want to get to know each other a bit more? Just us and a few good questions.',
  ],
  funny: [
    'Before we meet IRL — {len}-min Popin date? There are games, so zero awkward 😄',
    'Proof I’m not a catfish: a video date. Bonus mini games, no pressure 🐟',
    'Let’s skip 40 messages of “haha” and just do a Popin date 😂',
    'I promise to be at least 70% as charming on video. Popin date?',
  ],
  direct: [
    'Video date first? {len} minutes on Popin, guided. Pick a time here:',
    'Let’s meet on video before we share numbers. Here’s my Popin link:',
    'Quick Popin date this week? You pick the time.',
    'Up for a {len}-minute video date? One tap on Popin.',
  ],
  hinglish: [
    'Chai pe video date? Popin pe games bhi hain, awkward nahi hoga ☕',
    'Milne se pehle ek chhota sa video date? Promise, mazaa aayega 😄',
    'Number baad mein — pehle Popin pe thodi baat karein? ✨',
    'Ek {len}-min Popin date ho jaaye? Time tum choose karo 💛',
  ],
};

/** A catalog line with the length filled in. */
export function fillMessage(line: string, length: DateLength = 30): string {
  return line.replace(/\{len\}/g, String(length));
}

/** What gets copied or shared: the line, then the link. */
export function withLink(line: string, url: string): string {
  return `${line} ${url}`;
}

/** Every line, filled — for tests and the catalog check. */
export function allMessages(length: DateLength = 30): string[] {
  return TONES.flatMap((t) => READY_MESSAGES[t.id].map((l) => fillMessage(l, length)));
}
